import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { artUrl } from "@/lib/uploadArt";
import InkEstimateCard from "@/components/studio/InkEstimateCard";
import SaveToLibraryButton from "@/components/studio/SaveToLibraryButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Scissors, Loader2, FileDown, CheckCircle2, Link2, Link2Off } from "lucide-react";
import { toast } from "sonner";

const MODES = [
  { key: "shape", label: "Cut to shape", hint: "Exact geometric cut (square, circle, etc.)" },
  { key: "kiss_cut", label: "Kiss-Cut", hint: "Cut follows the figure silhouette (traced)" },
  { key: "cut_only", label: "Cut only", hint: "Cut each letter/shape, no background — carries transfer tape" },
  { key: "bleed_only", label: "Bleed only", hint: "Add bleed, no cut line (Paper / Booklet)" },
];
const SHAPES = [
  { key: "rectangle", label: "Rectangle" },
  { key: "rounded", label: "Rounded" },
  { key: "circle", label: "Circle" },
  { key: "rhombus", label: "Rhombus" },
];
const UNITS = ["in", "cm", "mm"];
const OUTPUTS = [
  { key: "pdf", label: "PDF", hint: "CutContour spot color — LF, Stickers & most machines" },
  { key: "png", label: "PNG (DTF)", hint: "Flattened raster at 300 DPI (150 for large jobs)" },
  { key: "svg", label: "SVG (Laser)", hint: "Vector cut line in solid black" },
];

// Shared production-file form. Provide either `file` (a File) OR `artId` (+ previewUrl).
export default function CutPrepForm({ file = null, artId = null, previewUrl = null,
  initW = "2", initH = "2", initUnit = "in", initMode = "shape", compact = false, onDone }) {
  const [unit, setUnit] = useState(initUnit);
  const [mode, setMode] = useState(initMode);
  const [shape, setShape] = useState("rectangle");
  const [fw, setFw] = useState(String(initW));
  const [fh, setFh] = useState(String(initH));
  const [ratioLock, setRatioLock] = useState(false);
  const ratioRef = useRef(0);
  const onW = (v) => { setFw(v); if (ratioLock && +v > 0 && ratioRef.current > 0) setFh(String(Math.round((+v / ratioRef.current) * 100) / 100)); };
  const onH = (v) => { setFh(v); if (ratioLock && +v > 0 && ratioRef.current > 0) setFw(String(Math.round((+v * ratioRef.current) * 100) / 100)); };
  const toggleRL = () => { const on = !ratioLock; setRatioLock(on); if (on) { const w = +fw, h = +fh; ratioRef.current = (w > 0 && h > 0) ? w / h : 1; } };
  const [bleed, setBleed] = useState("0.25");
  const [offset, setOffset] = useState("0.125");
  const [radius, setRadius] = useState("0.25");
  const [marks, setMarks] = useState("auto");
  const [outFmt, setOutFmt] = useState("pdf");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [livePreview, setLivePreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const previewTimer = useRef(null);
  const lastUrl = useRef(null);

  // Live cut preview: renders the actual detected/geometric cut line so the offset can be tuned before generating.
  useEffect(() => {
    if ((!file && !artId) || !(+fw > 0 && +fh > 0)) return;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      setPreviewing(true);
      try {
        const fd = new FormData();
        if (file) fd.append("file", file); else fd.append("art_id", artId);
        fd.append("final_w", fw); fd.append("final_h", fh); fd.append("unit", unit);
        fd.append("mode", mode); fd.append("shape", shape);
        fd.append("bleed", bleed || "0.25"); fd.append("offset", offset || "0.125");
        fd.append("corner_radius", radius || "0.25"); fd.append("margin_marks", marks);
        const res = await api.post("/production/preview-cut", fd, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        if (lastUrl.current) URL.revokeObjectURL(lastUrl.current);
        lastUrl.current = url;
        setLivePreview(url);
      } catch (e) { /* keep previous preview */ }
      setPreviewing(false);
    }, 450);
    return () => { if (previewTimer.current) clearTimeout(previewTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, artId, fw, fh, unit, mode, shape, bleed, offset, radius, marks]);

  useEffect(() => () => { if (lastUrl.current) URL.revokeObjectURL(lastUrl.current); }, []);

  const preview = previewUrl || (artId ? artUrl(artId) : null);
  const isContour = mode === "kiss_cut" || mode === "cut_only";
  const showCut = mode !== "bleed_only";

  const generate = async () => {
    if (!file && !artId) { toast.error("Upload artwork first"); return; }
    if (!(+fw > 0 && +fh > 0)) { toast.error("Enter a valid final size"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file); else fd.append("art_id", artId);
      fd.append("final_w", fw); fd.append("final_h", fh); fd.append("unit", unit);
      fd.append("mode", mode); fd.append("shape", shape);
      fd.append("bleed", bleed || "0.25"); fd.append("offset", offset || "0.125");
      fd.append("corner_radius", radius || "0.25"); fd.append("margin_marks", marks);
      fd.append("output_format", outFmt);
      const { data } = await api.post("/production/prep-cut", fd);
      setResult(data);
      onDone && onDone(data);
      toast.success("Production PDF ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build the production file");
    }
    setLoading(false);
  };

  // Approx preview: bleed = outer, cut = inner dashed (magenta = CutContour).
  const bb = +bleed || 0, W = (+fw || 1) + 2 * bb, H = (+fh || 1) + 2 * bb;
  const insetX = W ? (bb / W) * 100 : 0, insetY = H ? (bb / H) * 100 : 0;
  const cutColor = "#E6007E";

  return (
    <div className={compact ? "grid gap-4" : "grid md:grid-cols-2 gap-6"} data-testid="cutprep-form">
      <div className="space-y-4">
        {/* Cut mode */}
        <div>
          <label className="text-xs font-semibold text-slate-600">Cut mode</label>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {MODES.map((m) => (
              <button key={m.key} type="button" data-testid={`cut-mode-${m.key}`} onClick={() => { setMode(m.key); setResult(null); }} title={m.hint}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${mode === m.key ? "border-[#2495D3] bg-[#2495D3]/10 text-[#2495D3]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                {m.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-1.5">{MODES.find((m) => m.key === mode)?.hint}</p>
        </div>

        {/* Shape (only for cut to shape) */}
        {mode === "shape" && (
          <div>
            <label className="text-xs font-semibold text-slate-600">Shape</label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {SHAPES.map((s) => (
                <button key={s.key} type="button" data-testid={`cut-shape-${s.key}`} onClick={() => { setShape(s.key); setResult(null); }}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${shape === s.key ? "border-[#2495D3] bg-[#2495D3]/10 text-[#2495D3]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Size + unit */}
        <div className="flex items-end gap-2">
          <div className="flex-1"><label className="text-xs font-medium text-slate-500">Final width</label><Input data-testid="cut-w" type="number" value={fw} onChange={(e) => onW(e.target.value)} className="rounded-lg num mt-1" /></div>
          <button type="button" onClick={toggleRL} title={ratioLock ? "Aspect ratio locked" : "Lock aspect ratio"} className={`mb-2 ${ratioLock ? "text-[#2495D3]" : "text-slate-300 hover:text-slate-500"}`} data-testid="cut-ratio-lock">{ratioLock ? <Link2 size={16} /> : <Link2Off size={16} />}</button>
          <div className="flex-1"><label className="text-xs font-medium text-slate-500">Final height</label><Input data-testid="cut-h" type="number" value={fh} onChange={(e) => onH(e.target.value)} className="rounded-lg num mt-1" /></div>
          <div className="w-20">
            <label className="text-xs font-medium text-slate-500">Unit</label>
            <select data-testid="cut-unit" value={unit} onChange={(e) => setUnit(e.target.value)} className="mt-1 w-full h-9 rounded-lg border border-slate-300 text-sm px-2 bg-white">
              {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        </div>

        {/* Bleed / offset / radius */}
        <div className="grid grid-cols-3 gap-3">
          <div><label className="text-xs font-medium text-slate-500">Bleed (in)</label><Input data-testid="cut-bleed" type="number" step="0.001" value={bleed} onChange={(e) => setBleed(e.target.value)} className="rounded-lg num mt-1" /></div>
          {isContour && <div><label className="text-xs font-medium text-slate-500">Cut offset (in)</label><Input data-testid="cut-offset" type="number" step="0.001" value={offset} onChange={(e) => setOffset(e.target.value)} className="rounded-lg num mt-1" /></div>}
          {mode === "shape" && shape === "rounded" && <div><label className="text-xs font-medium text-slate-500">Corner radius (in)</label><Input data-testid="cut-radius" type="number" step="0.01" value={radius} onChange={(e) => setRadius(e.target.value)} className="rounded-lg num mt-1" /></div>}
          <div>
            <label className="text-xs font-medium text-slate-500">Margin marks</label>
            <select data-testid="cut-marks" value={marks} onChange={(e) => setMarks(e.target.value)} className="mt-1 w-full h-9 rounded-lg border border-slate-300 text-sm px-2 bg-white">
              <option value="auto">Auto (white bg)</option>
              <option value="on">Always on</option>
              <option value="off">Off</option>
            </select>
          </div>
        </div>

        {/* Output format */}
        <div>
          <label className="text-xs font-semibold text-slate-600">Output format</label>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {OUTPUTS.map((o) => (
              <button key={o.key} type="button" data-testid={`cut-out-${o.key}`} onClick={() => { setOutFmt(o.key); setResult(null); }} title={o.hint}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${outFmt === o.key ? "border-[#2495D3] bg-[#2495D3]/10 text-[#2495D3]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                {o.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-1.5">{OUTPUTS.find((o) => o.key === outFmt)?.hint}</p>
        </div>

        <Button data-testid="cut-generate" onClick={generate} disabled={loading} className="w-full rounded-lg bg-[#2495D3] hover:bg-[#1E7AA9]">
          {loading ? <><Loader2 size={15} className="mr-1.5 animate-spin" />Building PDF…</> : <><Scissors size={15} className="mr-1.5" />Generate production PDF</>}
        </Button>

        {result && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="cut-result">
            <p className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5"><CheckCircle2 size={16} />Ready · {result.media_w_in}×{result.media_h_in}&quot; (incl. bleed) · {(result.format || "pdf").toUpperCase()} @ {result.dpi} DPI</p>
            <p className="text-[11px] text-emerald-700 mt-0.5">{result.mode === "bleed_only" ? "Bleed only, no cut line." : (result.format === "png" ? "Flattened DTF raster." : `${result.cut_paths} cut path(s)`)} · Margin marks: {result.margin_marks ? "yes" : "no"}{result.white_bg ? " (white bg detected)" : ""}</p>
            <a href={artUrl(result.file_id)} target="_blank" rel="noreferrer" data-testid="cut-download">
              <Button className="mt-2 rounded-lg bg-emerald-600 hover:bg-emerald-700"><FileDown size={15} className="mr-1.5" />Download production {(result.format || "pdf").toUpperCase()}</Button>
            </a>
            <div className="mt-2"><SaveToLibraryButton fileId={result.file_id} defaultLabel={result.filename} /></div>
          </div>
        )}

        {(file || artId) && <InkEstimateCard file={file} fileId={artId} width={fw} height={fh} bleed={bleed} unit={unit} tab="prep" auto />}
      </div>

      {/* Preview */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex flex-col items-center justify-center min-h-[260px]" data-testid="cut-preview">
        {livePreview ? (
          <div className="w-full flex flex-col items-center">
            <div className="relative">
              <img src={livePreview} data-testid="cut-live-preview" alt="cut preview" className="max-h-[320px] w-auto max-w-full rounded-sm shadow-lg bg-white" />
              {previewing && <div className="absolute top-1.5 right-1.5 rounded-full bg-white/90 p-1 shadow"><Loader2 size={13} className="animate-spin text-[#2495D3]" /></div>}
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-[2px]" style={{ backgroundColor: cutColor }} />{showCut ? (isContour ? `Cut line · offset ${offset}"` : "Cut line") : "No cut"}</span>
              <span>Bleed {bleed}&quot;</span>
              <span>{W.toFixed(2)}×{H.toFixed(2)}&quot; sheet</span>
            </div>
          </div>
        ) : preview ? (
          <div className="relative shadow-lg" style={{ width: "100%", maxWidth: 360, aspectRatio: `${W} / ${H}` }}>
            <img src={preview} alt="artwork" className="absolute inset-0 w-full h-full object-cover rounded-sm" crossOrigin="anonymous" />
            <div className="absolute border border-white/70" style={{ inset: 0 }} />
            {showCut && (
              <div className="absolute border-2 border-dashed" style={{
                borderColor: cutColor,
                top: `${insetY}%`, left: `${insetX}%`, right: `${insetX}%`, bottom: `${insetY}%`,
                borderRadius: mode === "shape" && shape === "circle" ? "50%" : (mode === "shape" && shape === "rounded" ? "12px" : (isContour ? "24px" : "0"))
              }} />
            )}
            {previewing && <div className="absolute inset-0 flex items-center justify-center bg-white/40"><Loader2 size={20} className="animate-spin text-[#2495D3]" /></div>}
            <span className="absolute -top-5 left-0 text-[10px] text-slate-400">approx preview…</span>
          </div>
        ) : (
          <div className="text-center text-slate-400 text-sm"><Scissors size={28} className="mx-auto mb-2 opacity-40" />Preview of the cut &amp; bleed</div>
        )}
      </div>
    </div>
  );
}
